//#region src/errors/ai-sdk-error.ts
/**
* Symbol used for identifying AI SDK Error instances.
* Enables checking if an error is an instance of AISDKError across package versions.
*/
const marker$16 = "vercel.ai.error";
const symbol$16 = Symbol.for(marker$16);
/**
* Custom error class for AI SDK related errors.
* @extends Error
*/
var AISDKError = class AISDKError extends Error {
	/**
	* Creates an AI SDK Error.
	*
	* @param {Object} params - The parameters for creating the error.
	* @param {string} params.name - The name of the error.
	* @param {string} params.message - The error message.
	* @param {unknown} [params.cause] - The underlying cause of the error.
	*/
	constructor({ name, message, cause }) {
		super(message);
		this[symbol$16] = true;
		this.name = name;
		this.cause = cause;
	}
	/**
	* Checks if the given error is an AI SDK Error.
	* @param {unknown} error - The error to check.
	* @returns {boolean} True if the error is an AI SDK Error, false otherwise.
	*/
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$16);
	}
	static hasMarker(error, marker) {
		const markerSymbol = Symbol.for(marker);
		return error != null && typeof error === "object" && markerSymbol in error && typeof error[markerSymbol] === "boolean" && error[markerSymbol] === true;
	}
};
//#endregion
//#region src/errors/decision-unsupported-question-type-error.ts
const name$15 = "AI_DecisionUnsupportedQuestionTypeError";
const marker$15 = `vercel.ai.error.${name$15}`;
const symbol$15 = Symbol.for(marker$15);
/**
* A decision model does not support the type of a requested question.
*/
var DecisionUnsupportedQuestionTypeError = class extends AISDKError {
	constructor({ questionId, questionType, provider, modelId, message = `Question "${questionId}" has type "${questionType}", which is not supported by provider "${provider}" and model "${modelId}".` }) {
		super({
			name: name$15,
			message
		});
		this[symbol$15] = true;
		this.questionId = questionId;
		this.questionType = questionType;
		this.provider = provider;
		this.modelId = modelId;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$15);
	}
};
//#endregion
//#region src/errors/api-call-error.ts
const name$14 = "AI_APICallError";
const marker$14 = `vercel.ai.error.${name$14}`;
const symbol$14 = Symbol.for(marker$14);
var APICallError = class extends AISDKError {
	constructor({ message, url, requestBodyValues, statusCode, responseHeaders, responseBody, cause, isRetryable = statusCode != null && (statusCode === 408 || statusCode === 409 || statusCode === 429 || statusCode >= 500), data }) {
		super({
			name: name$14,
			message,
			cause
		});
		this[symbol$14] = true;
		this.url = url;
		this.requestBodyValues = requestBodyValues;
		this.statusCode = statusCode;
		this.responseHeaders = responseHeaders;
		this.responseBody = responseBody;
		this.isRetryable = isRetryable;
		this.data = data;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$14);
	}
};
//#endregion
//#region src/errors/empty-response-body-error.ts
const name$13 = "AI_EmptyResponseBodyError";
const marker$13 = `vercel.ai.error.${name$13}`;
const symbol$13 = Symbol.for(marker$13);
var EmptyResponseBodyError = class extends AISDKError {
	constructor({ message = "Empty response body" } = {}) {
		super({
			name: name$13,
			message
		});
		this[symbol$13] = true;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$13);
	}
};
//#endregion
//#region src/errors/decision-refusal-error.ts
const name$12 = "AI_DecisionRefusalError";
const marker$12 = `vercel.ai.error.${name$12}`;
const symbol$12 = Symbol.for(marker$12);
/**
* A decision model declined to answer one or more questions.
*/
var DecisionRefusalError = class extends AISDKError {
	constructor({ questionIds, provider, modelId, message = `Decision model "${modelId}" from provider "${provider}" refused ${questionIds.length === 1 ? "question" : "questions"} ${questionIds.map((id) => JSON.stringify(id)).join(", ")}.` }) {
		super({
			name: name$12,
			message
		});
		this[symbol$12] = true;
		this.questionIds = questionIds;
		this.provider = provider;
		this.modelId = modelId;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$12);
	}
};
//#endregion
//#region src/errors/get-error-message.ts
function getErrorMessage(error) {
	if (error == null) return "unknown error";
	if (typeof error === "string") return error;
	if (error instanceof Error) return error.toString();
	return JSON.stringify(error);
}
//#endregion
//#region src/errors/invalid-argument-error.ts
const name$11 = "AI_InvalidArgumentError";
const marker$11 = `vercel.ai.error.${name$11}`;
const symbol$11 = Symbol.for(marker$11);
/**
* A function argument is invalid.
*/
var InvalidArgumentError = class extends AISDKError {
	constructor({ message, cause, argument }) {
		super({
			name: name$11,
			message,
			cause
		});
		this[symbol$11] = true;
		this.argument = argument;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$11);
	}
};
//#endregion
//#region src/errors/invalid-prompt-error.ts
const name$10 = "AI_InvalidPromptError";
const marker$10 = `vercel.ai.error.${name$10}`;
const symbol$10 = Symbol.for(marker$10);
/**
* A prompt is invalid. This error should be thrown by providers when they cannot
* process a prompt.
*/
var InvalidPromptError = class extends AISDKError {
	constructor({ prompt, message, cause }) {
		super({
			name: name$10,
			message: `Invalid prompt: ${message}`,
			cause
		});
		this[symbol$10] = true;
		this.prompt = prompt;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$10);
	}
};
//#endregion
//#region src/errors/invalid-response-data-error.ts
const name$9 = "AI_InvalidResponseDataError";
const marker$9 = `vercel.ai.error.${name$9}`;
const symbol$9 = Symbol.for(marker$9);
/**
* Server returned a response with invalid data content.
* This should be thrown by providers when they cannot parse the response from the API.
*/
var InvalidResponseDataError = class extends AISDKError {
	constructor({ data, message = `Invalid response data: ${JSON.stringify(data)}.` }) {
		super({
			name: name$9,
			message
		});
		this[symbol$9] = true;
		this.data = data;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$9);
	}
};
//#endregion
//#region src/errors/json-parse-error.ts
const name$8 = "AI_JSONParseError";
const marker$8 = `vercel.ai.error.${name$8}`;
const symbol$8 = Symbol.for(marker$8);
var JSONParseError = class extends AISDKError {
	constructor({ text, cause }) {
		super({
			name: name$8,
			message: `JSON parsing failed: Text: ${text}.\nError message: ${getErrorMessage(cause)}`,
			cause
		});
		this[symbol$8] = true;
		this.text = text;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$8);
	}
};
//#endregion
//#region src/errors/load-api-key-error.ts
const name$7 = "AI_LoadAPIKeyError";
const marker$7 = `vercel.ai.error.${name$7}`;
const symbol$7 = Symbol.for(marker$7);
var LoadAPIKeyError = class extends AISDKError {
	constructor({ message }) {
		super({
			name: name$7,
			message
		});
		this[symbol$7] = true;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$7);
	}
};
//#endregion
//#region src/errors/load-setting-error.ts
const name$6 = "AI_LoadSettingError";
const marker$6 = `vercel.ai.error.${name$6}`;
const symbol$6 = Symbol.for(marker$6);
var LoadSettingError = class extends AISDKError {
	constructor({ message }) {
		super({
			name: name$6,
			message
		});
		this[symbol$6] = true;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$6);
	}
};
//#endregion
//#region src/errors/no-content-generated-error.ts
const name$5 = "AI_NoContentGeneratedError";
const marker$5 = `vercel.ai.error.${name$5}`;
const symbol$5 = Symbol.for(marker$5);
/**
* Thrown when the AI provider fails to generate any content.
*/
var NoContentGeneratedError = class extends AISDKError {
	constructor({ message = "No content generated." } = {}) {
		super({
			name: name$5,
			message
		});
		this[symbol$5] = true;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$5);
	}
};
//#endregion
//#region src/errors/no-such-model-error.ts
const name$4 = "AI_NoSuchModelError";
const marker$4 = `vercel.ai.error.${name$4}`;
const symbol$4 = Symbol.for(marker$4);
var NoSuchModelError = class extends AISDKError {
	constructor({ errorName = name$4, modelId, modelType, message = `No such ${modelType}: ${modelId}` }) {
		super({
			name: errorName,
			message
		});
		this[symbol$4] = true;
		this.modelId = modelId;
		this.modelType = modelType;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$4);
	}
};
//#endregion
//#region src/errors/no-such-provider-reference-error.ts
const name$3 = "AI_NoSuchProviderReferenceError";
const marker$3 = `vercel.ai.error.${name$3}`;
const symbol$3 = Symbol.for(marker$3);
/**
* Thrown when a provider reference cannot be resolved because the specified
* provider is not found in the provider reference mapping.
*/
var NoSuchProviderReferenceError = class extends AISDKError {
	constructor({ provider, reference, message = `No provider reference found for provider '${provider}'. Available providers: ${Object.keys(reference).join(", ")}` }) {
		super({
			name: name$3,
			message
		});
		this[symbol$3] = true;
		this.provider = provider;
		this.reference = reference;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$3);
	}
};
//#endregion
//#region src/errors/too-many-embedding-values-for-call-error.ts
const name$2 = "AI_TooManyEmbeddingValuesForCallError";
const marker$2 = `vercel.ai.error.${name$2}`;
const symbol$2 = Symbol.for(marker$2);
var TooManyEmbeddingValuesForCallError = class extends AISDKError {
	constructor(options) {
		super({
			name: name$2,
			message: `Too many values for a single embedding call. The ${options.provider} model "${options.modelId}" can only embed up to ${options.maxEmbeddingsPerCall} values per call, but ${options.values.length} values were provided.`
		});
		this[symbol$2] = true;
		this.provider = options.provider;
		this.modelId = options.modelId;
		this.maxEmbeddingsPerCall = options.maxEmbeddingsPerCall;
		this.values = options.values;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$2);
	}
};
//#endregion
//#region src/errors/type-validation-error.ts
const name$1 = "AI_TypeValidationError";
const marker$1 = `vercel.ai.error.${name$1}`;
const symbol$1 = Symbol.for(marker$1);
var TypeValidationError = class TypeValidationError extends AISDKError {
	constructor({ value, cause, context }) {
		let contextPrefix = "Type validation failed";
		if (context?.field) contextPrefix += ` for ${context.field}`;
		if (context?.entityName || context?.entityId) {
			contextPrefix += " (";
			const parts = [];
			if (context.entityName) parts.push(context.entityName);
			if (context.entityId) parts.push(`id: "${context.entityId}"`);
			contextPrefix += parts.join(", ");
			contextPrefix += ")";
		}
		super({
			name: name$1,
			message: `${contextPrefix}: Value: ${JSON.stringify(value)}.\nError message: ${getErrorMessage(cause)}`,
			cause
		});
		this[symbol$1] = true;
		this.value = value;
		this.context = context;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$1);
	}
	/**
	* Wraps an error into a TypeValidationError.
	* If the cause is already a TypeValidationError with the same value and context, it returns the cause.
	* Otherwise, it creates a new TypeValidationError.
	*
	* @param {Object} params - The parameters for wrapping the error.
	* @param {unknown} params.value - The value that failed validation.
	* @param {unknown} params.cause - The original error or cause of the validation failure.
	* @param {TypeValidationContext} params.context - Optional context about what is being validated.
	* @returns {TypeValidationError} A TypeValidationError instance.
	*/
	static wrap({ value, cause, context }) {
		if (TypeValidationError.isInstance(cause) && cause.value === value && cause.context?.field === context?.field && cause.context?.entityName === context?.entityName && cause.context?.entityId === context?.entityId) return cause;
		return new TypeValidationError({
			value,
			cause,
			context
		});
	}
};
//#endregion
//#region src/errors/unsupported-functionality-error.ts
const name = "AI_UnsupportedFunctionalityError";
const marker = `vercel.ai.error.${name}`;
const symbol = Symbol.for(marker);
var UnsupportedFunctionalityError = class extends AISDKError {
	constructor({ functionality, message = `'${functionality}' functionality not supported.` }) {
		super({
			name,
			message
		});
		this[symbol] = true;
		this.functionality = functionality;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker);
	}
};
//#endregion
//#region src/errors/index.ts
/** @deprecated Use `Experimental_DecisionUnsupportedQuestionTypeError` instead. */
const Experimental_EvaluationUnsupportedQuestionTypeError = DecisionUnsupportedQuestionTypeError;
//#endregion
//#region src/json-value/is-json.ts
function isJSONValue(value) {
	if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") return true;
	if (Array.isArray(value)) return value.every(isJSONValue);
	if (typeof value === "object") return Object.entries(value).every(([key, val]) => typeof key === "string" && (val === void 0 || isJSONValue(val)));
	return false;
}
function isJSONArray(value) {
	return Array.isArray(value) && value.every(isJSONValue);
}
function isJSONObject(value) {
	return value != null && typeof value === "object" && Object.entries(value).every(([key, val]) => typeof key === "string" && (val === void 0 || isJSONValue(val)));
}
//#endregion
export { AISDKError, APICallError, EmptyResponseBodyError, DecisionRefusalError as Experimental_DecisionRefusalError, DecisionUnsupportedQuestionTypeError as Experimental_DecisionUnsupportedQuestionTypeError, Experimental_EvaluationUnsupportedQuestionTypeError, InvalidArgumentError, InvalidPromptError, InvalidResponseDataError, JSONParseError, LoadAPIKeyError, LoadSettingError, NoContentGeneratedError, NoSuchModelError, NoSuchProviderReferenceError, TooManyEmbeddingValuesForCallError, TypeValidationError, UnsupportedFunctionalityError, getErrorMessage, isJSONArray, isJSONObject, isJSONValue };

//# sourceMappingURL=index.js.map